import { Button, Divider, Stack, Typography, useTheme } from "@mui/material";
import {
	CameraIcon,
	FolderOpenIcon,
	LockIcon,
	type LucideIcon,
	UploadIcon,
} from "lucide-react";
import type { FC } from "react";
import type { ObjPathProxy } from "ts-object-path";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DocumentEmptyStateStep } from "web_myplann/Portfolio/Components/Redesign/Documents/DocumentEmptyStateStep";
import { RedesignListBackground } from "web_myplann/Shared/Components/Redesign/RedesignListBackground";

const STEP_COPY = Resources.Portfolio.List.Redesign.Documents.EmptyState.Steps;

type Step = {
	id: string;
	icon: LucideIcon;
	label: ObjPathProxy<unknown, string>;
	description: ObjPathProxy<unknown, string>;
};

const DOCUMENT_STEPS: Step[] = [
	{
		id: "photo",
		icon: CameraIcon,
		label: STEP_COPY.photoLabel,
		description: STEP_COPY.photoDesc,
	},
	{
		id: "organized",
		icon: FolderOpenIcon,
		label: STEP_COPY.organizedLabel,
		description: STEP_COPY.organizedDesc,
	},
];

type Props = {
	onAddDocument: () => void;
};

export const DocumentsEmptyState: FC<Props> = ({ onAddDocument }) => {
	const { t } = useResource();
	const theme = useTheme();

	const emptyState = Resources.Portfolio.List.Redesign.Documents.EmptyState;

	return (
		<RedesignListBackground px={2.5} pt={3}>
			<Stack alignItems="center" mb={2.5}>
				<Stack
					alignItems="center"
					justifyContent="center"
					width={80}
					height={80}
					borderRadius="24px"
					sx={{
						background: theme.gradients.iconBadge,
					}}
				>
					<FolderOpenIcon size={40} color={theme.palette.darkmodeText.white} />
				</Stack>
			</Stack>

			<Typography variant="h3" textAlign="center" mb={0.75}>
				{t(emptyState.headline)}
			</Typography>
			<Typography variant="body2" textAlign="center" mb={3}>
				{t(emptyState.description)}
			</Typography>

			<Stack
				mb={3}
				borderRadius="16px"
				bgcolor="background.paper"
				sx={{ border: `1px solid ${theme.palette.grey[100]}` }}
				divider={<Divider sx={{ borderColor: theme.palette.grey[50] }} />}
			>
				{DOCUMENT_STEPS.map((step, index) => (
					<DocumentEmptyStateStep
						key={step.id}
						index={index}
						icon={step.icon}
						label={t(step.label)}
						description={t(step.description)}
					/>
				))}
			</Stack>

			<Button
				variant="primary"
				startIcon={<UploadIcon size={18} />}
				onClick={onAddDocument}
				sx={{ mb: 1.5 }}
			>
				{t(emptyState.primaryCta)}
			</Button>

			<Stack
				direction="row"
				alignItems="center"
				justifyContent="center"
				gap={1}
			>
				<LockIcon size={theme.iconSize.xs} color={theme.palette.grey[400]} />
				<Typography variant="hint">{t(emptyState.trustBadge)}</Typography>
			</Stack>
		</RedesignListBackground>
	);
};
